/* ============================================================
   FEED ENGINE (feed.js)
   ------------------------------------------------------------
   Owns: rendering slides, the story-bubble filter row,
   scroll-snap navigation, autoplay exclusivity, the global
   sound toggle, and progress/seek for mixed-length content.

   It never touches data directly beyond reading the arrays in
   js/data.js, and it delegates all social/commerce behavior
   (likes, comments, share, merch sheet) to interactions.js.

   SHOPIFY THEME NOTE: in a theme, this file ships unchanged as
   an asset; only data.js is replaced by Liquid-rendered JSON.
   ============================================================ */

const Feed = (() => {
  /* ---- state ---- */
  const state = {
    filter: 'trending',   // Trending is the resting/default feed
    subFilter: null,      // album key when the Albums sub-row is in use
    socialOpen: false,    // Social bubble reveal row
    items: [],            // items currently rendered
    players: new Map(),   // itemId -> VimeoPlayer
    activeId: null,       // itemId of the slide currently in view
    muted: true,          // global sound state (starts muted for autoplay)
  };

  /* ---- dom refs (populated by init) ---- */
  let $feed, $bubbles, $subBubbles, $soundToggle;

  /* Sponsor credit — appended as the last line of every caption.
     Drop Monster's OFFICIAL logo (from their partner brand kit) at
     assets/logos/monster.png and it appears automatically next to
     the text; until then the text stands alone in Monster green. */
  const SPONSOR_HTML = `
      <a class="meta__sponsor" href="https://www.monsterenergy.com/en-us/music/obed-padilla/"
         target="_blank" rel="noopener"><img class="meta__sponsor-logo"
        src="${ASSET('assets/logos/monster.png')}" onerror="this.remove()" alt="">
        Powered by Monster Energy</a>`;

  /* =========================================================
     STORY BUBBLES (filter row)
     ========================================================= */
  function renderBubbles() {
    $bubbles.innerHTML = '';
    FILTERS.forEach(f => {
      const locked = f.gated && !Interactions.isUnlocked();
      const b = document.createElement('button');
      b.className = 'bubble'
        + (!f.link && state.filter === f.key ? ' bubble--active' : '')
        + (f.gated ? ' bubble--gated' : '');
      b.dataset.filter = f.key;
      b.type = 'button';
      /* A11Y: name + state for screen readers — the visible label
         is inside spans with decorative emoji, so set it plainly. */
      b.setAttribute('aria-label',
        f.label + (locked ? ' (locked — sign up to unlock)' : ''));
      if (!f.link) {
        b.setAttribute('aria-current',
          state.filter === f.key ? 'true' : 'false');
      }
      // DESIGNER CONVENTION: assets/icons/{key}.png replaces the
      // emoji automatically when the file exists — the emoji is
      // hidden on successful load (transparent icons would otherwise
      // show it behind them) and stays visible only when no icon
      // file exists (onerror removes the img). Zero code changes.
      const iconHtml = `<span class="bubble__emoji">${f.icon}</span><img class="bubble__img"
        src="${ASSET(f.iconImg || `assets/icons/${f.key}.png`)}"
        onload="this.previousElementSibling.style.visibility='hidden'"
        onerror="this.remove()" alt="">`;
      b.innerHTML = `
        <span class="bubble__ring"><span class="bubble__icon">${iconHtml}</span>
          ${locked ? '<span class="bubble__lock">🔒</span>' : ''}</span>
        <span class="bubble__label">${f.label}</span>`;
      if (f.link) {
        // straight link (Business Inquiries) — not a feed filter
        b.addEventListener('click', () => { window.location.href = f.link; });
      } else {
        b.addEventListener('click', () => setFilter(f.key));
      }
      $bubbles.appendChild(b);
    });
  }

  /* ---- Music sub-navigation ----
     Tapping the Music bubble opens a second row of bubbles: every
     album/EP in ALBUMS plus a LIVE SHOWS entry. Tapping one filters
     the feed to that album (or to live sets); tapping it again
     returns to "all music". Leaving the Music tab closes the row.
     Still zero navigation — the same feed filters in place.        */

  /** body classes that other UI reads: taller chrome offset while any
      sub-row is open; cart button only within the Merch tab. */
  function syncChromeState() {
    document.body.classList.toggle('subnav-open',
      state.filter === 'music');
    // cart button only exists in the in-feed merch flow — in forward
    // mode every merch tap goes to the real store, so no cart here
    document.body.classList.toggle('merch-open',
      state.filter === 'merch' && CONFIG.merchMode !== 'forward');
  }

  /* Live Shows rides inside the Music sub-row */
  const MUSIC_LIVE_KEY = '__live';

  function renderSubBubbles() {
    const open = state.filter === 'music';
    $subBubbles.innerHTML = '';
    $subBubbles.classList.toggle('bubbles--open', open);
    syncChromeState();
    if (!open) return;
    const entries = [
      ...ALBUMS.map(a => ({ ...a, _album: true })),
      { key: MUSIC_LIVE_KEY, title: 'Live Shows', short: 'Live Shows', mark: '⚡' },
    ];
    entries.forEach(a => {
      const b = document.createElement('button');
      b.className = 'subbubble' + (state.subFilter === a.key ? ' subbubble--active' : '');
      b.title = a.title;
      b.type = 'button';
      b.setAttribute('aria-label', a.title);
      b.setAttribute('aria-pressed',
        state.subFilter === a.key ? 'true' : 'false');
      /* SHOPIFY: swap the monogram <span> for real cover art —
         <img src="{{ album.cover | image_url }}"> from the album
         metaobject. Styling already handles a square image.        */
      b.innerHTML = `
        <span class="subbubble__art">${
          a.cover ? `<img src="${ASSET(a.cover)}" alt="">` : a.mark
        }</span>
        <span class="subbubble__label">${a.short}</span>`;
      b.addEventListener('click', () => setSubFilter(a.key));
      $subBubbles.appendChild(b);
    });
  }

  function setSubFilter(key) {
    state.subFilter = state.subFilter === key ? null : key; // tap again → all albums
    renderSubBubbles();
    renderFeed();
    syncUrl();
  }

  /** Filter the SAME feed in place — no navigation. */
  function setFilter(key) {
    if (state.filter === key) key = 'trending';   // tap again → back to Trending
    state.filter = key;
    state.subFilter = null;                  // reset album selection
    renderBubbles();
    renderSubBubbles();
    renderFeed();
    syncUrl();
  }

  /* =========================================================
     SHAREABLE URLS (programmer feedback, 2026-10-06; clean
     paths per Benton, same day)
       obedpadilla.com/tour       → the Tour tab
       obedpadilla.com/v/<id>     → that post, in the feed
     Shopify can't serve arbitrary paths from a theme, so each
     clean path also exists as a Shopify URL REDIRECT to the
     query form (/tour → /?tab=tour, /v/h12 → /?v=h12). On
     arrival the router reads either form and puts the clean
     path back in the address bar (history.replaceState — no
     reloads, no history spam). Old ?tab=/?v= links keep
     working forever. NOTE for content syncs: every new feed
     item needs a matching /v/<id> redirect (urlRedirectCreate)
     or its cold-loaded share link 404s.
     ========================================================= */
  const REDUCED_MOTION = !!(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* tab key ↔ clean path segment (only where they differ) */
  const TAB_PATHS = { social: 'socials' };
  const tabToPath = key => TAB_PATHS[key] || key;
  const pathToTab = seg => {
    for (const k in TAB_PATHS) if (TAB_PATHS[k] === seg) return k;
    return seg;
  };

  function syncUrl() {
    if (!window.history || !history.replaceState) return;
    try {
      let path = '/';
      if (state.filter && state.filter !== 'trending') {
        path = '/' + tabToPath(state.filter);
      } else if (state.activeId) {
        path = '/v/' + encodeURIComponent(state.activeId);
      }
      history.replaceState(null, '', path);
    } catch (_) { /* privacy modes can refuse — links still work */ }
  }

  /** Apply the deep link from the address bar once, on boot.
      Accepts clean paths (/tour, /v/<id>) and the legacy query
      forms (?tab=, ?v=) that the Shopify redirects land on. */
  function applyRoute() {
    let p;
    try { p = new URLSearchParams(location.search); } catch (_) { return; }
    let tab = p.get('tab');
    let v = p.get('v');
    const segs = location.pathname.split('/').filter(Boolean);
    if (!tab && !v && segs.length) {
      if (segs[0] === 'v' && segs[1]) {
        v = decodeURIComponent(segs[1]);
      } else if (segs.length === 1) {
        const key = pathToTab(segs[0]);
        if (FILTERS.some(f => f.key === key && !f.link)) tab = key;
      }
    }
    if (tab && tab !== state.filter &&
        FILTERS.some(f => f.key === tab && !f.link)) {
      setFilter(tab);
    }
    if (!v) return;
    const target = $feed.querySelector(`.slide[data-id="${CSS.escape(v)}"]`);
    if (!target) return;   // gated / not in this tab — open normally
    /* A deep link means the fan asked for a specific post — skip
       the pre-swipe intro and land on it with the chrome visible. */
    document.body.classList.remove('pre-swipe');
    const hint = document.getElementById('swipeHint');
    if (hint) hint.remove();
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  /* =========================================================
     FEED RENDERING
     ========================================================= */
  function renderFeed() {
    // tear down old players
    state.players.forEach(p => p.destroy());
    state.players.clear();
    state.activeId = null;
    $feed.innerHTML = '';

    // Gated tab (Backstage Pass): show the join wall until the fan
    // has signed up via Laylo. Gated content NEVER leaks into 'all'.
    const activeDef = FILTERS.find(f => f.key === state.filter) || {};
    if (activeDef.gated && !Interactions.isUnlocked()) {
      state.items = [];
      $feed.appendChild(buildLockedSlide(activeDef));
      window.scrollTo(0, 0);
      observeSlides();
      return;
    }

    // Socials tab — a link panel, not a feed
    if (state.filter === 'social') {
      state.items = [];
      $feed.appendChild(buildSocialsSlide());
      window.scrollTo(0, 0);
      observeSlides();
      return;
    }

    // Tour tab — a swipeable list of shows, not a feed
    if (state.filter === 'tour') {
      state.items = [];
      buildTourListSlides().forEach(s => $feed.appendChild(s));
      window.scrollTo(0, 0);
      observeSlides();
      return;
    }

    const gatedKeys = new Set(FILTERS.filter(f => f.gated).map(f => f.key));
    const isHome = state.filter === 'trending' || state.filter === 'all';
    state.items = FEED_ITEMS.filter(it => {
      // Music = the old Albums + Live Shows categories combined
      const inCategory = isHome
        ? !gatedKeys.has(it.category)   // home feeds show everything ungated
        : state.filter === 'music'
        ? (it.category === 'albums' || it.category === 'live')
        : it.category === state.filter;
      // sub-filter (only meaningful on the Music tab)
      if (inCategory && state.filter === 'music' && state.subFilter) {
        return state.subFilter === MUSIC_LIVE_KEY
          ? it.category === 'live'
          : it.album === state.subFilter;
      }
      return inCategory;
    });

    /* HOME FEED CADENCE (Benton, 2026-09-11):
         P1 video (Benton's pick, js/feed-priority.js)
         → merch ad
         → 3 Priority-2 videos (popular on social / live performance)
         → repeat (merch ad → 3 P2s …) until content runs out.
       P2 pool = live clips + vertical hosted clips + #Shorts, ranked
       by likes (social-popularity proxy), hosted beating YouTube on
       ties. Everything else fills in after the P2 pool runs dry,
       shuffled, keeping the same ad rhythm. */
    if (state.filter === 'trending') {
      const rankIds = typeof TRENDING_ORDER !== 'undefined' ? TRENDING_ORDER : [];
      const rank = new Map(rankIds.map((id, i) => [id, i]));
      const byRank = arr => arr.slice().sort((a, b) =>
        (rank.has(a.id) ? rank.get(a.id) : 1e9) -
        (rank.has(b.id) ? rank.get(b.id) : 1e9));
      const isAd = it => it.type === 'product' || it.type === 'tourdates';
      const shuffle = arr => {
        const a = arr.slice();
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };

      // Priority 1 — Benton's chosen opener(s), in his order
      const p1Ids = typeof FEED_P1 !== 'undefined' ? FEED_P1 : [];
      const p1q = p1Ids
        .map(id => state.items.find(it => it.id === id)).filter(Boolean);
      const pool = state.items.filter(it =>
        !isAd(it) && !p1Ids.includes(it.id));

      // Priority 2 — popular-on-social / live-performance clips.
      // FEED_P2_FIRST (js/feed-priority.js, from Obed's real TikTok/
      // IG traction) jumps the queue in its own order; the rest of
      // the pool ranks by likes.
      const isP2 = it => it.type === 'video' && (
        it.category === 'live' ||
        (it.hostedSrc && it.orientation === 'vertical') ||
        (it.tags || []).includes('#Shorts'));
      const p2FirstIds = typeof FEED_P2_FIRST !== 'undefined' ? FEED_P2_FIRST : [];
      const p2First = p2FirstIds
        .map(id => pool.find(it => it.id === id)).filter(Boolean);
      const poolRest = pool.filter(it => !p2FirstIds.includes(it.id));
      const p2q = p2First.concat(poolRest.filter(isP2).sort((a, b) =>
        (b.likes || 0) - (a.likes || 0) ||
        (b.hostedSrc ? 1 : 0) - (a.hostedSrc ? 1 : 0)));
      const restq = shuffle(poolRest.filter(it => !isP2(it)));

      // Merch ads, cycling forever in ranked order
      const merchAds = byRank(FEED_ITEMS.filter(it => it.type === 'product'
        && it.category !== 'backstage'));
      const mu = { n: 0 };
      const nextMerchAd = () => {
        if (!merchAds.length) return null;
        const src = merchAds[mu.n % merchAds.length];
        const clone = mu.n < merchAds.length ? src
          : { ...src, id: `${src.id}-r${Math.floor(mu.n / merchAds.length)}` };
        mu.n++;
        return clone;
      };

      const merged = [];
      while (p1q.length || p2q.length || restq.length) {
        if (p1q.length) merged.push(p1q.shift());
        if (p2q.length || restq.length) {
          const ad = nextMerchAd();
          if (ad) merged.push(ad);
        }
        for (let k = 0; k < 3; k++) {
          const v = p2q.shift() || restq.shift();
          if (v) merged.push(v);
        }
      }
      state.items = merged;
    }

    if (!state.items.length) {
      $feed.innerHTML = `<section class="slide slide--empty">
        <p>Nothing here yet — check back soon.</p></section>`;
      return;
    }

    state.items.forEach(item => {
      const slide =
        item.type === 'product'   ? buildProductSlide(item)   :
        item.type === 'photos'    ? buildPhotoSlide(item)     :
        item.type === 'ticket'    ? buildTicketSlide(item)    :
        item.type === 'tourdates' ? buildTourDatesSlide(item) :
        item.type === 'showad'    ? buildShowAdSlide(item)    :
        item.type === 'dm'        ? buildDmSlide(item)        :
        item.type === 'bwelcome'  ? buildWelcomeSlide(item)   :
        item.type === 'capsule'   ? buildCapsuleSlide(item)   :
        item.type === 'teaser'    ? buildTeaserSlide(item)    :
        item.type === 'audioclip' ? buildAudioClipSlide(item) :
        buildVideoSlide(item);
      /* A11Y (2026-10-06): every slide announces what it is —
         screen readers get the title + caption instead of an
         unnamed region of buttons. */
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label',
        [item.title, item.caption].filter(Boolean).join(' — ') ||
        CONFIG.artistName);
      // sponsor credit = last line of every caption block
      slide.querySelector('.slide__meta')
        ?.insertAdjacentHTML('beforeend', SPONSOR_HTML);
      // MERCH TAB (Benton, 2026-09-09): pure shopping — no likes,
      // comments, or share on product slides inside the Merch tab.
      // (The bag/shop rail button stays; in-feed ads keep the rail.)
      if (state.filter === 'merch') {
        slide.querySelectorAll(
          '[data-action="like"], [data-action="comment"], [data-action="share"]'
        ).forEach(btn => btn.remove());
      }
      $feed.appendChild(slide);
    });

    // soft fade-in on filter change
    $feed.classList.remove('feed--enter');
    void $feed.offsetWidth;               // restart animation
    $feed.classList.add('feed--enter');

    window.scrollTo(0, 0);
    observeSlides();
  }

  /* ---- video slide ---- */
  function buildVideoSlide(item) {
    const el = document.createElement('section');
    // orientation: 'vertical' → full-bleed cover crop;
    // anything else (default) → natural 16:9, letterboxed in
    // portrait, full-screen when the phone rotates to landscape.
    const horizontal = item.orientation !== 'vertical';
    el.className = 'slide slide--video' + (horizontal ? ' slide--horizontal' : '');
    el.dataset.id = item.id;

    const isLong = (item.duration || 0) >= CONFIG.longFormThresholdSec;

    el.innerHTML = `
      <div class="video-shell"></div>
      <div class="slide__scrim"></div>

      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified" title="Verified">✓</span></div>
        <div class="meta__title">${item.title}</div>
        <div class="meta__caption">${item.caption}</div>
        <div class="meta__tags">${(item.tags || []).join(' ')}</div>
      </div>

      <div class="slide__badges">
        ${item.exclusive ? `<span class="badge badge--exclusive">🎟 BACKSTAGE</span>` : ''}
        ${isLong ? `<span class="badge badge--full">FULL SET</span>` : ''}
        <span class="badge badge--time">${formatDuration(item.duration)}</span>
      </div>

      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
        <button class="rail__btn rail__fs" data-action="fullscreen" aria-label="Fullscreen">
          <span class="rail__icon">⛶</span>
          <span class="rail__count">Full</span>
        </button>
      </div>

      <button class="slide__playhint" aria-label="Play/Pause"></button>

      <div class="progress ${isLong ? 'progress--long' : ''}" role="slider"
           aria-label="Seek" tabindex="0">
        <div class="progress__track"><div class="progress__fill"></div></div>
        ${isLong ? `<div class="progress__times">
            <span class="progress__now">0:00</span>
            <span class="progress__total">${formatDuration(item.duration)}</span>
          </div>` : ''}
      </div>`;

    // Player mounts into .video-shell.
    // hostedSrc → HostedPlayer (Shopify-CDN MP4 — native, no iframe)
    // youtubeId → YouTubePlayer (real catalog)
    // vimeoId   → VimeoPlayer   (legacy option)
    // All expose the same interface, so nothing below cares which.
    const shell = el.querySelector('.video-shell');
    const fill = el.querySelector('.progress__fill');
    const nowEl = el.querySelector('.progress__now');
    const timeBadge = el.querySelector('.badge--time');

    const callbacks = {
      onTimeUpdate: ({ seconds, percent, duration }) => {
        fill.style.width = (percent * 100).toFixed(2) + '%';
        if (nowEl) nowEl.textContent = formatDuration(seconds);
        // duration wasn't in data (real YouTube items) → fill the
        // badge from the player once known
        if (timeBadge && !timeBadge.textContent && duration) {
          timeBadge.textContent = formatDuration(duration);
        }
      },
      onPlay:  () => el.classList.remove('slide--paused'),
      onPause: () => el.classList.add('slide--paused'),
    };
    /* LAZY PLAYERS (mobile stability fix, 2026-09-02): players are
       NOT created at render time. With 40+ slides, mounting every
       <video>/<iframe> up front exhausts mobile Safari's media
       memory and crashes the page after a couple of swipes. Each
       video slide instead exposes mount/unmount hooks, and a
       windowing observer (observeSlides) keeps only the slides near
       the viewport live — everything else is torn down.           */
    el._mountPlayer = () => {
      let p = state.players.get(item.id);
      if (p) return p;
      p = item.hostedSrc
        ? new HostedPlayer(shell, item, callbacks)
        : item.youtubeId
        ? new YouTubePlayer(shell, item.youtubeId, callbacks)
        : new VimeoPlayer(shell, item.vimeoId, callbacks);
      state.players.set(item.id, p);
      return p;
    };
    el._unmountPlayer = () => {
      const p = state.players.get(item.id);
      if (!p) return;
      p.destroy();
      state.players.delete(item.id);
      el.classList.add('slide--paused');
      fill.style.width = '0%';
    };
    /* IFRAME FACADE (iOS Chrome crash fix, 2026-09-28): a stretch of
       consecutive YouTube slides used to put 3-4 full iframe players
       in memory at once (windowing keeps ±1 viewport mounted), which
       blows WKWebView's memory ceiling on iPhones — the page dies
       with Chrome's "Can't open this page". Embed slides (YouTube /
       Vimeo) that are merely NEAR the viewport now mount only a
       thumbnail poster; the real iframe is created when the slide
       actually activates, and every other live embed is destroyed at
       that moment (see activateSlide). Native hosted <video> is
       cheap and keeps the original windowing. */
    el._isEmbed = !item.hostedSrc && !!(item.youtubeId || item.vimeoId);
    el._mountPoster = () => {
      if (!item.youtubeId || shell.querySelector('.video-poster')) return;
      const img = document.createElement('img');
      img.className = 'video-poster';
      img.alt = '';
      img.loading = 'lazy';
      img.src = 'https://i.ytimg.com/vi/' + item.youtubeId + '/hqdefault.jpg';
      img.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000';
      shell.prepend(img);
    };
    const getPlayer = () => el._mountPlayer();

    // tap video → play/pause (TikTok behavior)
    el.querySelector('.slide__playhint').addEventListener('click', () => {
      const paused = el.classList.contains('slide--paused');
      paused ? getPlayer().play() : getPlayer().pause();
    });

    /* Fullscreen toggle — fullscreens the whole slide (video +
       progress bar + rail stay usable). Hidden on browsers with no
       element-fullscreen API (e.g. iPhone Safari — there, rotating
       the phone is the theater view). */
    const fsBtn = el.querySelector('[data-action="fullscreen"]');
    const fsSupported = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!fsSupported) fsBtn.style.display = 'none';
    else {
      const fsLabel = fsBtn.querySelector('.rail__count');
      fsBtn.addEventListener('click', () => {
        const current = document.fullscreenElement || document.webkitFullscreenElement;
        if (current) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        else (el.requestFullscreen || el.webkitRequestFullscreen).call(el);
      });
      document.addEventListener('fullscreenchange', () => {
        const on = document.fullscreenElement === el;
        fsLabel.textContent = on ? 'Exit' : 'Full';
        el.classList.toggle('slide--fs', on);
      });
    }

    // tap/drag the progress bar → seek (essential for full-length shows)
    const prog = el.querySelector('.progress');
    const seekFromEvent = ev => {
      const x = (ev.touches ? ev.touches[0] : ev).clientX;
      const r = prog.getBoundingClientRect();
      getPlayer().seekToPercent((x - r.left) / r.width);
    };
    prog.addEventListener('click', seekFromEvent);
    prog.addEventListener('touchmove', ev => { ev.preventDefault(); seekFromEvent(ev); },
      { passive: false });

    wireRail(el, item);
    return el;
  }

  /* ---- shared carousel wiring (photo posts + product slides) ---- */
  function wireCarousel(el, count) {
    const car = el.querySelector('.carousel');
    const dots = el.querySelectorAll('.carousel__dot');
    const counter = el.querySelector('.carousel__counter');
    car.addEventListener('scroll', () => {
      const idx = Math.round(car.scrollLeft / car.clientWidth);
      if (counter) counter.textContent = `${idx + 1}/${count}`;
      dots.forEach((d, i) => d.classList.toggle('carousel__dot--on', i === idx));
    }, { passive: true });
    return car;
  }

  /* ---- product slide (Merch mode) ----
     IG-style product carousel: photo of the product → on-model
     shot → sizes & buy panel, all swiped sideways inside the
     slide. Products without photos fall back to the line-art
     card. Photos are PLACEHOLDER stock until real product
     photography comes from the live store CDN (js/products.js). */
  /** Hand the fan to the real store: that product's page on
      obedpadilla.com, with the tapped size preselected when we have
      one. Same-tab navigation = the traditional merch experience;
      the browser back button returns to the feed. */
  function goToStore(product, variant) {
    const url = variant?.id
      ? `${product.url}?variant=${variant.id}`
      : product.url;
    window.location.href = url;
  }

  function buildProductSlide(item) {
    const product = PRODUCTS[item.productId];
    if (product.photos?.length) return buildProductCarouselSlide(item, product);
    const el = document.createElement('section');
    el.className = 'slide slide--product';
    el.dataset.id = item.id;
    el.style.setProperty('--accent', product.accent);

    el.innerHTML = `
      <div class="product-stage">
        <div class="product-visual">
          <div class="product-visual__glow"></div>
          <!-- productArtSVG (cart.js): line-art mark, automatically
               replaced by the real photo once product.image is set -->
          <div class="product-visual__art">${productArtSVG(product)}</div>
          ${product.badge ? `<span class="product-visual__badge">${product.badge}</span>` : ''}
        </div>
        <div class="product-info">
          <div class="product-info__name">${product.name}</div>
          <div class="product-info__price">$${product.price}</div>
          <div class="product-info__desc">${product.description}</div>
          <button class="product-cta" data-action="shop">
            Tap to shop <span class="product-cta__arrow">→</span>
          </button>
        </div>
      </div>
      <div class="slide__scrim slide__scrim--product"></div>

      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified">✓</span></div>
        <div class="meta__caption">${item.caption}</div>
      </div>

      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;

    // FORWARD mode → stage/CTA go straight to the store's product
    // page; IN-FEED mode → the expand-to-buy sheet.
    const shopAction = CONFIG.merchMode === 'forward'
      ? () => goToStore(product)
      : () => Interactions.openMerchSheet(product);
    el.querySelector('.product-cta').addEventListener('click', shopAction);
    el.querySelector('.product-visual').addEventListener('click', shopAction);

    wireRail(el, item);
    return el;
  }

  /* ---- photo carousel slide (IG-style: swipe SIDEWAYS through
          photos inside a slide, while the feed swipes vertically).
          Horizontal scroll-snap — same tech as the feed itself, so
          it works on any phone browser. `images` comes from the
          sheet (image URLs in the video_link column, type=photos). */
  function buildPhotoSlide(item) {
    const el = document.createElement('section');
    el.className = 'slide slide--photos';
    el.dataset.id = item.id;
    el.innerHTML = `
      <div class="carousel">
        ${item.images.map((src, i) => `
          <div class="carousel__frame">
            <img class="carousel__img" src="${src}" alt="${item.title} — photo ${i + 1}"
                 loading="${i === 0 ? 'eager' : 'lazy'}"/>
          </div>`).join('')}
      </div>
      <div class="carousel__counter">1/${item.images.length}</div>
      <div class="carousel__dots">
        ${item.images.map((_, i) =>
          `<span class="carousel__dot${i === 0 ? ' carousel__dot--on' : ''}"></span>`).join('')}
      </div>
      <div class="slide__scrim"></div>

      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified">✓</span></div>
        <div class="meta__title">${item.title}</div>
        <div class="meta__caption">${item.caption}</div>
        <div class="meta__tags">${(item.tags || []).join(' ')}</div>
      </div>

      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;

    // dots + counter track the horizontal position
    const car = el.querySelector('.carousel');
    const dots = el.querySelectorAll('.carousel__dot');
    const counter = el.querySelector('.carousel__counter');
    car.addEventListener('scroll', () => {
      const idx = Math.round(car.scrollLeft / car.clientWidth);
      counter.textContent = `${idx + 1}/${item.images.length}`;
      dots.forEach((d, i) => d.classList.toggle('carousel__dot--on', i === idx));
    }, { passive: true });

    wireRail(el, item);
    return el;
  }

  /* ---- product slide with photo carousel + sizes panel ---- */
  function buildProductCarouselSlide(item, product) {
    const el = document.createElement('section');
    el.className = 'slide slide--product slide--product-carousel';
    el.dataset.id = item.id;
    el.style.setProperty('--accent', product.accent);
    const frames = product.photos.length + 1;   // photos + sizes panel

    el.innerHTML = `
      <div class="carousel">
        ${product.photos.map((src, i) => `
          <div class="carousel__frame pframe-photo">
            <div class="pframe-photo__bg"
                 style="background-image:url('${shopifyImg(src, 180)}')"></div>
            <img class="carousel__img pframe-photo__img" src="${shopifyImg(src, 900)}"
                 alt="${product.name} — ${i === 0 ? 'product' : 'worn'}"
                 loading="lazy"/>
          </div>`).join('')}
        <div class="carousel__frame pframe-sizes">
          <div class="psizes">
            ${product.badge ? `<span class="product-visual__badge psizes__badge">${product.badge}</span>` : ''}
            <div class="psizes__name">${product.name}</div>
            <div class="psizes__price">$${product.price}</div>
            <p class="psizes__desc">${product.description}</p>
            <div class="merch__label">Size</div>
            <div class="merch__sizes psizes__sizes"></div>
            <button class="btn btn--gold btn--full psizes__add" disabled>Select a size</button>
            <a class="psizes__store" href="${product.url}" target="_blank"
               rel="noopener">View on obedpadilla.com ↗</a>
          </div>
        </div>
      </div>
      <div class="carousel__counter">1/${frames}</div>
      <div class="carousel__dots">
        ${Array.from({ length: frames }, (_, i) =>
          `<span class="carousel__dot${i === 0 ? ' carousel__dot--on' : ''}"></span>`).join('')}
      </div>
      <div class="slide__scrim"></div>
      ${product.badge ? `<div class="slide__badges">
        <span class="badge badge--exclusive">${product.badge}</span></div>` : ''}

      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified">✓</span></div>
        <div class="meta__title">${product.name} · $${product.price}</div>
        <div class="meta__caption">${item.caption}</div>
        <div class="meta__tags">Tap photo → view in store · swipe ← for sizes</div>
      </div>

      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
        <button class="rail__btn rail__shop" data-action="shop" aria-label="Shop">
          <span class="rail__icon">🛍</span>
          <span class="rail__count">$${product.price}</span>
        </button>
      </div>`;

    const car = wireCarousel(el, frames);

    const forward = CONFIG.merchMode === 'forward';
    let chosen = null;
    const sizesBox = el.querySelector('.psizes__sizes');
    const addBtn = el.querySelector('.psizes__add');

    // size chips: FORWARD mode → tapping a size hands the fan to the
    // real product page with that size preselected (?variant=).
    // IN-FEED mode → original select-then-add-to-bag flow.
    product.variants.forEach(v => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'size' + (v.available ? '' : ' size--soldout');
      b.textContent = v.label;
      b.disabled = !v.available;
      b.addEventListener('click', () => {
        if (forward) { goToStore(product, v); return; }
        chosen = v;
        sizesBox.querySelectorAll('.size').forEach(x => x.classList.remove('size--on'));
        b.classList.add('size--on');
        addBtn.disabled = false;
        addBtn.textContent = `Add to bag · $${product.price}`;
      });
      sizesBox.appendChild(b);
    });
    if (forward) {
      addBtn.disabled = false;
      addBtn.textContent = 'Shop on obedpadilla.com ↗';
      addBtn.addEventListener('click', () => goToStore(product, chosen));
    } else {
      addBtn.addEventListener('click', () => {
        if (chosen) Cart.add(product, chosen);
      });
    }

    // bag button on the rail: FORWARD → straight to the product page;
    // IN-FEED → jump the carousel to the sizes frame
    el.querySelector('[data-action="shop"]').addEventListener('click', () =>
      forward
        ? goToStore(product)
        : car.scrollTo({ left: car.clientWidth * (frames - 1), behavior: REDUCED_MOTION ? 'auto' : 'smooth' }));

    // tapping the product photo → that product's page on the real
    // store (obedpadilla.com/products/{handle}). Swiping still swipes.
    el.querySelectorAll('.pframe-photo__img').forEach(img =>
      img.addEventListener('click', () => goToStore(product)));

    wireRail(el, item);
    return el;
  }

  /* ---- SHOW AD slide — the "shows are coming" reminder card ----
     One per upcoming show, rotated into the every-3rd/4th-post ad
     slots. Two looks:
       • show.flyer set (a poster image URL/path) → the flyer runs
         full-bleed with the ticket CTA over it
       • no flyer → a designed EP-palette announcement card built
         from the tour-dates row (date / city / venue / status)
     Add a flyer to any show in js/tour-dates.js: flyer: 'assets/
     flyers/oside.jpg' — drop the image in assets/flyers/.          */
  function buildShowAdSlide(item) {
    const s = item.show;
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--showad';
    el.dataset.id = item.id;
    const statusMap = { onsale: 'Tickets on sale', low: 'Low stock',
      soldout: 'Sold out', announced: 'Announced', free: 'Free show' };
    const cta = s.status === 'soldout' ? 'Sold out'
      : s.status === 'free' ? 'It’s free — pull up' : 'Get tickets';

    el.innerHTML = `
      ${s.flyer ? `
        <div class="showad__flyerwrap">
          <div class="showad__flyerbg" style="background-image:url('${s.flyer}')"></div>
          <img class="showad__flyer" src="${s.flyer}" alt="Show flyer — ${s.city}"/>
        </div>` : `
        <div class="showad__card">
          <div class="showad__eyebrow">🎟 LIVE — ON THE ROAD</div>
          <div class="showad__date">${s.date}</div>
          <div class="showad__city">${s.city}</div>
          <div class="showad__venue">${s.venue}</div>
          <span class="tour-status tour-status--${s.status === 'soldout' ? 'soldout' : s.status === 'announced' ? 'announced' : 'onsale'}">${statusMap[s.status] || 'Announced'}</span>
        </div>`}
      <div class="slide__scrim"></div>

      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified">✓</span></div>
        <div class="meta__title">${s.city} · ${s.date}</div>
        <div class="meta__caption">Catch Obed live. Be in the room.</div>
        ${s.link && s.status !== 'soldout'
          ? `<a class="btn btn--gold showad__cta" href="${s.link}"
               target="_blank" rel="noopener">${cta} →</a>` : ''}
      </div>

      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;

    wireRail(el, item);
    return el;
  }

  /* ---- Backstage: locked wall slide ---- */
  function buildLockedSlide(def) {
    const el = document.createElement('section');
    el.className = 'slide slide--locked';
    el.innerHTML = `
      <div class="locked">
        <div class="locked__ticket">🎟</div>
        <div class="locked__title">Backstage Pass</div>
        <p class="locked__text">Early songs. Early merch. Presale tickets.
          A direct line to Obed.<br/>Join the list once — it's all yours.</p>
        <button class="btn btn--gold locked__cta">Get your pass</button>
        <div class="locked__perks">
          <span>♫ First listens</span><span>🛍 Early drops</span>
          <span>🎫 Presales</span><span>✉ DM Obed</span>
        </div>
      </div>`;
    el.querySelector('.locked__cta')
      .addEventListener('click', () => Interactions.openFanForm('backstage'));
    return el;
  }

  /* ---- Backstage: presale ticket slide ---- */
  function buildTicketSlide(item) {
    const p = item.presale;
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--ticket';
    el.dataset.id = item.id;
    if (p.comingSoon) {
      // COMING SOON state (Benton, 2026-09-15): live shell, real
      // presale shows + code drop in the moment Benton sends them.
      el.innerHTML = `
        <div class="card-stage">
          <div class="ticket">
            <div class="ticket__head">
              <span class="badge badge--exclusive">🎟 BACKSTAGE</span>
              <div class="ticket__title">${item.title}</div>
              <div class="ticket__sub">COMING SOON</div>
            </div>
            <p class="locked__text">${item.caption} As a pass holder,
              your code lands here 48 hours before the public.</p>
          </div>
        </div>
        <div class="rail">
          <button class="rail__btn rail__like" data-action="like" aria-label="Like">
            <span class="rail__icon">♥</span>
            <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
          </button>
        </div>`;
      wireRail(el, item);
      return el;
    }
    el.innerHTML = `
      <div class="card-stage">
        <div class="ticket">
          <div class="ticket__head">
            <span class="badge badge--exclusive">🎟 BACKSTAGE</span>
            <div class="ticket__title">${item.title}</div>
            <div class="ticket__sub">Opens ${p.opens} · 48h before public</div>
          </div>
          <div class="ticket__shows">
            ${p.shows.map(s => `
              <div class="ticket__show">
                <span class="ticket__date">${s.date}</span>
                <span class="ticket__city">${s.city}</span>
                <span class="ticket__venue">${s.venue}</span>
              </div>`).join('')}
          </div>
          <!-- PRODUCTION: swap the copy-code flow for per-show
               presale links (Ticketmaster/AXS presale URL + code),
               or a Shopify discount code for ticket products. -->
          <button class="btn btn--gold btn--full ticket__cta">
            Reveal presale code</button>
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;
    const cta = el.querySelector('.ticket__cta');
    cta.addEventListener('click', () => {
      cta.textContent = p.code;
      cta.classList.add('ticket__cta--code');
      Interactions.copyText(p.code, `Presale code ${p.code} copied`);
    });
    wireRail(el, item);
    return el;
  }

  /* ---- Tour: live Bandsintown dates card ----
     Renders a skeleton immediately; bandsintown.js fills it with
     live data (or the join-Backstage empty state) async.        */
  function buildTourDatesSlide(item) {
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--tourdates';
    el.dataset.id = item.id;
    el.innerHTML = `
      <div class="card-stage">
        <div class="ticket bit-card">
          <div class="ticket__head">
            <span class="badge badge--bit">● LIVE DATES</span>
            <div class="ticket__title">${item.title}</div>
            <div class="ticket__sub">${item.caption}</div>
          </div>
          <div class="bit-body">
            <div class="bit-loading">Checking the road…</div>
          </div>
          <div class="bit-credit">KEM Tour Desk ·
            <a class="bit-credit__link" target="_blank" rel="noopener"
               href="https://www.bandsintown.com/a/${CONFIG.bandsintown.artistId}">also on Bandsintown</a></div>
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;
    // KEM's own dates (js/tour-dates.js) are the source of truth;
    // Bandsintown is only the fallback when that list is empty.
    const body = el.querySelector('.bit-body');
    if (typeof TourDates !== 'undefined' && TourDates.hasDates()) TourDates.render(body);
    else Bandsintown.load(body);
    wireRail(el, item);
    return el;
  }

  /* ---- Tour tab: pure show list (Benton, 2026-09-09) ----
     Not a feed. Shows render as a clean list, chunked five per
     slide so a deep tour swipes vertically like everything else.
     No likes, no comments, no captions, no share.               */
  function buildTourListSlides() {
    const shows = (typeof TOUR_DATES !== 'undefined' ? TOUR_DATES : []);
    if (!shows.length) {
      const empty = document.createElement('section');
      empty.className = 'slide slide--card slide--tourlist';
      empty.dataset.id = 'tourlist-0';
      empty.innerHTML = `
        <div class="card-stage"><div class="ticket">
          <div class="ticket__head">
            <span class="badge badge--bit">● ON THE ROAD</span>
            <div class="ticket__title">No shows on the books</div>
            <div class="ticket__sub">Join Backstage for first word on new dates.</div>
          </div>
        </div></div>`;
      return [empty];
    }
    const STATUS = { onsale: 'Tickets', low: 'Low stock',
      soldout: 'Sold out', announced: 'Soon', free: 'Free show' };
    const PER = 5;
    const slides = [];
    for (let i = 0; i < shows.length; i += PER) {
      const chunk = shows.slice(i, i + PER);
      const el = document.createElement('section');
      el.className = 'slide slide--card slide--tourlist';
      el.dataset.id = `tourlist-${slides.length}`;
      el.innerHTML = `
      <div class="card-stage"><div class="ticket bit-card">
          <div class="ticket__head">
            <span class="badge badge--bit">● ON THE ROAD</span>
            <div class="ticket__title">Tour</div>
            ${shows.length > PER ? `<div class="ticket__sub">
              ${i + 1}–${i + chunk.length} of ${shows.length} ·
              swipe for more</div>` : ''}
          </div>
          <div class="ticket__shows bit-shows">
            ${chunk.map(s => {
              const cls = s.status === 'soldout' ? 'soldout'
                : s.status === 'announced' ? 'announced' : 'onsale';
              const inner = `
                <span class="ticket__date">${s.date}</span>
                <span class="ticket__city">${s.city}</span>
                <span class="ticket__venue">${s.venue} ·
                  <em class="tour-status tour-status--${cls}">${STATUS[s.status] || 'Soon'}</em></span>`;
              return s.link && s.status !== 'soldout'
                ? `<a class="ticket__show bit-show" href="${s.link}"
                     target="_blank" rel="noopener">${inner}</a>`
                : `<div class="ticket__show">${inner}</div>`;
            }).join('')}
          </div>
          <div class="bit-credit">KEM Tour Desk ·
            <a class="bit-credit__link" target="_blank" rel="noopener"
               href="https://www.bandsintown.com/a/${CONFIG.bandsintown.artistId}">also on Bandsintown</a></div>
        </div></div>`;
      slides.push(el);
    }
    return slides;
  }

  /* ---- Socials tab: icon link panel (Benton, 2026-09-09) ----
     Not a feed — one panel, every profile, big and obvious.
     Rows come from SOCIALS in data.js.                         */
  function buildSocialsSlide() {
    const ICONS = {
      spotify: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M7 10.5c3-.9 7-.6 9.5 1M7.5 13.2c2.6-.7 5.6-.4 7.8 1M8 15.7c2-.5 4.3-.3 6 .7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
      youtube: '<svg viewBox="0 0 24 24"><rect x="2.8" y="6.2" width="18.4" height="11.6" rx="3.4" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M10.3 9.7v4.6l4.3-2.3z" fill="currentColor"/></svg>',
      instagram: '<svg viewBox="0 0 24 24"><rect x="3.2" y="3.2" width="17.6" height="17.6" rx="4.6" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="17.2" cy="6.8" r="1.2" fill="currentColor"/></svg>',
      tiktok: '<svg viewBox="0 0 24 24"><path d="M14.5 3.5c.4 2.6 2 4.2 4.5 4.5v3c-1.7 0-3.2-.5-4.5-1.4v5.9a5.6 5.6 0 1 1-5.6-5.6c.3 0 .7 0 1 .1v3.1a2.6 2.6 0 1 0 1.6 2.4V3.5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>',
    };
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--socials';
    el.dataset.id = 'socials';
    el.innerHTML = `
      <div class="card-stage"><div class="socials">
        <div class="socials__head">
          <div class="socials__title">Obed, everywhere</div>
          <div class="socials__sub">Follow along — same handle across the board.</div>
        </div>
        ${SOCIALS.map(s => `
          <a class="socials__row" href="${s.url}" target="_blank" rel="noopener">
            <span class="socials__icon">${ICONS[s.key] || '@'}</span>
            <span class="socials__body">
              <span class="socials__label">${s.label}</span>
              <span class="socials__handle">${s.handle}</span>
            </span>
            <span class="socials__go">↗</span>
          </a>`).join('')}
      </div></div>`;
    return el;
  }

  /* ---- Backstage: welcome-from-Obed slide (Benton, 2026-09-14) ----
     The first thing a new Backstage member sees. Ships as a designed
     card; the moment Obed records his thank-you video, this item
     becomes type "video" with a hostedSrc and drops straight in. */
  function buildWelcomeSlide(item) {
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--bwelcome';
    el.dataset.id = item.id;
    el.innerHTML = `
      <div class="card-stage">
        <div class="dm-card bwelcome">
          <span class="badge badge--exclusive">🎟 BACKSTAGE</span>
          <div class="dm-card__avatar">OP</div>
          <div class="dm-card__title">${item.title}</div>
          <p class="dm-card__text">${item.caption}</p>
          <button class="btn btn--gold bwelcome__cta" data-room="lounge">
            🎤 Step into the Green Room</button>
          <button class="btn btn--ghost bwelcome__cta" data-room="dm">
            ✉️ Ask Obed — drop him a question</button>
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
      </div>`;
    el.querySelectorAll('.bwelcome__cta').forEach(btn =>
      btn.addEventListener('click', () =>
        Interactions.openDm(btn.dataset.room)));
    wireRail(el, item);
    return el;
  }

  /* ---- Backstage: merch-capsule teaser slide (Benton, 2026-09-14) ----
     Capsule-first merch: cover art full-bleed, COMING SOON, and the
     "you're signed up for early access" promise. When the capsule
     goes live, this card flips to the real product drop. */
  function buildCapsuleSlide(item) {
    const c = item.capsule || {};
    const cover = c.cover ? ASSET(c.cover) : null;
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--capsule';
    el.dataset.id = item.id;
    el.innerHTML = `
      ${cover ? `<div class="capsule__bg" style="background-image:url('${cover}')"></div>` : ''}
      <div class="slide__scrim"></div>
      <div class="card-stage">
        <div class="capsule">
          <div class="capsule__eyebrow">🛍 ${c.eyebrow || 'CAPSULE'} — ${c.status || 'COMING SOON'}</div>
          ${cover ? `<img class="capsule__cover" src="${cover}" alt="${c.name} capsule artwork"/>` : ''}
          <div class="capsule__name">${c.name || item.title}</div>
          <div class="capsule__items">${c.itemsLine || ''}</div>
          <div class="capsule__early">🎟 You're signed up for early access</div>
        </div>
      </div>
      <div class="slide__meta">
        <div class="meta__handle">${CONFIG.artistHandle}
          <span class="meta__verified">✓</span></div>
        <div class="meta__caption">${item.caption}</div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;
    wireRail(el, item);
    return el;
  }

  /* ---- Coming-soon teaser slide (e.g. Rodeo Clown Act II) ----
     One designed post: cover art, COMING SOON, and a Backstage
     CTA — "sign up for early access" for the public, an
     early-access chip for members. Reuses the capsule styling. */
  function buildTeaserSlide(item) {
    const t = item.teaser || {};
    const cover = t.cover ? ASSET(t.cover) : null;
    const unlocked = Interactions.isUnlocked();
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--capsule slide--teaser';
    el.dataset.id = item.id;
    el.innerHTML = `
      ${cover ? `<div class="capsule__bg" style="background-image:url('${cover}')"></div>` : ''}
      <div class="slide__scrim"></div>
      <div class="card-stage">
        <div class="capsule">
          <div class="capsule__eyebrow">🤡 ${t.eyebrow || item.title} — ${t.status || 'COMING SOON'}</div>
          ${cover ? `<img class="capsule__cover" src="${cover}" alt="${item.title} artwork"/>` : ''}
          <div class="capsule__name">${t.name || item.title}</div>
          <div class="capsule__items">${item.caption}</div>
          ${t.cta === false ? ''
            : unlocked
            ? `<div class="capsule__early">🎟 You have early access</div>`
            : `<button class="btn btn--gold teaser__cta">🎟 Sign up for Backstage — early access</button>`}
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;
    el.querySelector('.teaser__cta')
      ?.addEventListener('click', () => Interactions.openFanForm('backstage'));
    wireRail(el, item);
    return el;
  }

  /* ---- Backstage: 30-second first-listen audio slide ----
     Apple-Music-style waveform that autoplays on swipe (following
     the global sound state, like every video) and colors in as the
     clip plays. Tapping the waveform plays/pauses — and unmutes if
     sound was off. Capped at item.clipLength seconds. */
  function buildAudioClipSlide(item) {
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--audioclip slide--paused';
    el.dataset.id = item.id;
    const BARS = 44;
    const clipLen = item.clipLength || 30;
    const bars = Array.from({ length: BARS }, (_, i) => {
      const h = 16 + Math.abs(Math.sin(i * 12.9898 + 4.1414) * 0.65 +
                              Math.sin(i * 0.7) * 0.35) * 80;
      return `<span class="wave__bar" style="height:${h.toFixed(0)}%"></span>`;
    }).join('');
    el.innerHTML = `
      <div class="card-stage">
        <div class="audioclip">
          <span class="badge badge--exclusive">🎟 BACKSTAGE — FIRST LISTEN</span>
          <div class="audioclip__title">${item.title}</div>
          <div class="audioclip__sub">${item.caption || ''}</div>
          <button class="audioclip__wave" aria-label="Play / pause">
            <span class="audioclip__playhint">▶</span>${bars}</button>
          <div class="audioclip__times">
            <span class="audioclip__now">0:00</span>
            <span class="audioclip__total">0:${String(clipLen).padStart(2, '0')}</span>
          </div>
          <div class="audioclip__hint">🔊 sound's up top if it's quiet</div>
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn rail__comment" data-action="comment" aria-label="Comments">
          <span class="rail__icon">💬</span>
          <span class="rail__count">${Interactions.commentCount(item.id)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;

    const audio = new Audio();
    audio.src = item.audioSrc;
    audio.preload = 'metadata';
    const $bars = el.querySelectorAll('.wave__bar');
    const $now = el.querySelector('.audioclip__now');
    const fmt = s => `0:${String(Math.floor(s)).padStart(2, '0')}`;
    audio.addEventListener('timeupdate', () => {
      if (audio.currentTime >= clipLen) {          // 30s cap → reset
        audio.pause();
        audio.currentTime = 0;
        el.classList.add('slide--paused');
        return;
      }
      const on = Math.floor((audio.currentTime / clipLen) * BARS);
      $bars.forEach((b, i) => b.classList.toggle('wave__bar--on', i < on));
      $now.textContent = fmt(audio.currentTime);
    });
    const player = {
      play: () => audio.play()
        .then(() => el.classList.remove('slide--paused'))
        .catch(() => el.classList.add('slide--paused')),
      pause: () => { audio.pause(); el.classList.add('slide--paused'); },
      setMuted: m => { audio.muted = m; },
      seekToPercent: p => { audio.currentTime = Math.min(clipLen, p * clipLen); },
      destroy: () => { audio.pause(); audio.removeAttribute('src'); audio.load(); },
    };
    el._mountPlayer = () => {
      if (!state.players.has(item.id)) state.players.set(item.id, player);
   return player;
    };
    el._unmountPlayer = () => {
      if (!state.players.has(item.id)) return;
      audio.pause();
      state.players.delete(item.id);
    };
    el.querySelector('.audioclip__wave').addEventListener('click', () => {
      if (audio.paused) {
        if (state.muted) toggleSound();   // a first-listen deserves sound
        player.setMuted(false);
        player.play();
      } else {
        player.pause();
      }
    });
    wireRail(el, item);
    return el;
  }

  /* ---- Backstage: DM Obed slide ---- */
  function buildDmSlide(item) {
    const el = document.createElement('section');
    el.className = 'slide slide--card slide--dm';
    el.dataset.id = item.id;
    el.innerHTML = `
      <div class="card-stage">
        <div class="dm-card">
          <span class="badge badge--exclusive">🎟 BACKSTAGE</span>
          <div class="dm-card__avatar">OP</div>
          <div class="dm-card__title">${item.title}</div>
          <p class="dm-card__text">${item.caption}</p>
          <button class="btn btn--gold dm-card__cta">Step inside</button>
        </div>
      </div>
      <div class="rail">
        <button class="rail__btn rail__like" data-action="like" aria-label="Like">
          <span class="rail__icon">♥</span>
          <span class="rail__count">${Interactions.formatCount(item.likes)}</span>
        </button>
        <button class="rail__btn" data-action="share" aria-label="Share">
          <span class="rail__icon">↗</span>
          <span class="rail__count">Share</span>
        </button>
      </div>`;
    el.querySelector('.dm-card__cta')
      .addEventListener('click', () => Interactions.openDm());
    el.querySelector('[data-action="like"]')
      .addEventListener('click', ev => Interactions.toggleLike(item, ev.currentTarget));
    el.querySelector('[data-action="share"]')
      .addEventListener('click', () => Interactions.share(item));
    return el;
  }

  /* ---- shared rail wiring ---- */
  function wireRail(el, item) {
    el.querySelector('[data-action="like"]')
      ?.addEventListener('click', ev => Interactions.toggleLike(item, ev.currentTarget));
    el.querySelector('[data-action="comment"]')
      ?.addEventListener('click', () => Interactions.openComments(item));
    el.querySelector('[data-action="share"]')
      ?.addEventListener('click', () => Interactions.share(item));
    // posts with no native sound can carry a looping song clip
    if (item.audioSrc && !el._mountPlayer) attachAmbientAudio(el, item);
  }

  /* =========================================================
     AMBIENT AUDIO (roadmap #1 — Benton, 2026-10-12)
     Photo dumps, teasers and cards carry a looping ~30s song
     clip via item.audioSrc (a pre-clipped file on the Shopify
     CDN). It rides the exact same rails as every video player:
     mounted/unmounted by the windowing observer, started by
     activateSlide, muted until the fan taps sound on, and torn
     down when the slide drifts away. audioStart (seconds,
     optional) offsets playback for files that aren't pre-cut.
     ========================================================= */
  function attachAmbientAudio(el, item) {
    el._mountPlayer = () => {
      let p = state.players.get(item.id);
      if (p) return p;
      const audio = new Audio(item.audioSrc);
      audio.loop = true;
      audio.preload = 'metadata';
      audio.muted = true;
      const start = item.audioStart || 0;
      if (start) {
        audio.addEventListener('loadedmetadata', () => {
          try { audio.currentTime = start; } catch (_) {}
        });
        // manual loop back to the start offset
        audio.loop = false;
        audio.addEventListener('ended', () => {
          try { audio.currentTime = start; } catch (_) {}
          audio.play().catch(() => {});
        });
      }
      p = {
        play:  () => audio.play().catch(() => {}),
        pause: () => audio.pause(),
        setMuted: m => { audio.muted = m; },
        seekToPercent: () => {},
        destroy: () => {
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
        },
      };
      state.players.set(item.id, p);
      return p;
    };
    el._unmountPlayer = () => {
      const p = state.players.get(item.id);
      if (!p) return;
      p.destroy();
      state.players.delete(item.id);
    };
  }

  /* =========================================================
     AUTOPLAY EXCLUSIVITY (IntersectionObserver)
     Only the slide filling ≥60% of the viewport plays; all
     others pause. Handles fast flicks and slow drags alike.
     ========================================================= */
  let observer = null;
  let mountObserver = null;
  function observeSlides() {
    observer?.disconnect();
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const id = entry.target.dataset.id;
        if (entry.intersectionRatio >= 0.6) activateSlide(id);
        else if (id === state.activeId && entry.intersectionRatio < 0.4) {
          state.players.get(id)?.pause();
        }
      });
    }, { root: null, threshold: [0.4, 0.6] });

    /* PLAYER WINDOWING — mount players only for slides within ~1
       viewport of the screen; tear them down once they drift away.
       Keeps live <video>/<iframe> count at ~3 regardless of feed
       length — the fix for mobile Safari's crash-after-two-swipes. */
    mountObserver?.disconnect();
    mountObserver = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.target._mountPlayer) return;
        if (e.isIntersecting) {
          // Embed slides get only a poster while nearby — the real
          // iframe mounts on activation (activateSlide).
          if (e.target._isEmbed) e.target._mountPoster?.();
          else e.target._mountPlayer();
        } else e.target._unmountPlayer();
      });
    }, { root: null, rootMargin: '120% 0px', threshold: 0 });

    $feed.querySelectorAll('.slide').forEach(s => {
      observer.observe(s);
      mountObserver.observe(s);
    });
  }

  function activateSlide(id) {
    if (state.activeId === id) return;
    // pause the previous one
    if (state.activeId) state.players.get(state.activeId)?.pause();
    state.activeId = id;
    const slideEl = $feed.querySelector(`.slide[data-id="${CSS.escape(id)}"]`);
    /* ONE-EMBED RULE (iOS Chrome crash fix): when an embed slide
       activates, every other live embed iframe is torn down first,
       so at most one YouTube/Vimeo player exists at a time. Their
       posters stay behind, so swiping back still looks instant. */
    if (slideEl?._isEmbed) {
      $feed.querySelectorAll('.slide').forEach(s => {
        if (s !== slideEl && s._isEmbed && state.players.has(s.dataset.id)) {
          s._unmountPlayer();
        }
      });
    }
    // lazy world: the active slide's player may not exist yet
    let player = state.players.get(id);
    if (!player) {
      player = slideEl?._mountPlayer?.();
    }
    if (player) {
      player.setMuted(state.muted);
      player.play();
    }
    syncUrl();   // the address bar always points at the active post
  }

  /* =========================================================
     SOUND TOGGLE (global — persists across slides)
     ========================================================= */
  function toggleSound() {
    state.muted = !state.muted;
    $soundToggle.classList.toggle('sound--on', !state.muted);
    $soundToggle.setAttribute('aria-label', state.muted ? 'Unmute' : 'Mute');
    $soundToggle.innerHTML = state.muted ? '🔇' : '🔊';
    state.players.forEach(p => p.setMuted(state.muted));
    // first unmute = the attention treatment has done its job
    if (!state.muted) {
      $soundToggle.classList.remove('sound--attn');
      document.getElementById('soundHint')?.remove();
    }
  }

  /* SOUND-BUTTON ATTENTION (Benton, 2026-09-14): the speaker
     pulses a gold glow until the fan unmutes once. A subtle,
     borderless "tap for sound" note sits to the LEFT of the
     button, shows EVERY TIME the chrome pops up (first reveal
     and each wake from quiet mode), and fades on its own after
     a few seconds. Gone forever once the fan unmutes. */
  let soundHintTimer = null;
  function showSoundHint() {
    if (!state.muted) return;
    const h = document.getElementById('soundHint');
    if (!h) return;
    h.classList.remove('gone');
    clearTimeout(soundHintTimer);
    soundHintTimer = setTimeout(() => h.classList.add('gone'), 5000);
  }
  function wireSoundAttention() {
    if (!state.muted) return;
    $soundToggle.classList.add('sound--attn');
    const hint = document.createElement('div');
    hint.id = 'soundHint';
    hint.className = 'gone';
    hint.textContent = 'tap for sound';
    $soundToggle.parentElement.insertBefore(hint, $soundToggle);
    if (document.body.classList.contains('pre-swipe')) {
      // show once the first swipe reveals the chrome
      const onReveal = () => {
        if (document.body.classList.contains('pre-swipe')) return;
        window.removeEventListener('scroll', onReveal);
        showSoundHint();
      };
      window.addEventListener('scroll', onReveal, { passive: true });
    } else {
      showSoundHint();
    }
  }

  /* =========================================================
     KEYBOARD NAV (desktop nicety — ↑/↓ move between slides)
     ========================================================= */
  function wireKeyboard() {
    window.addEventListener('keydown', e => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (document.body.classList.contains('modal-open')) return;
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      window.scrollBy({ top: dir * window.innerHeight, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });
  }

  /* =========================================================
     QUIET MODE — 2.5 seconds after load / last interaction, all
     chrome except the logo, the action rail, and the caption
     fades out so the video breathes. Any touch or click (or a
     feed scroll) brings it back and restarts the timer.
     ========================================================= */
  let quietTimer = null;
  function wakeUi() {
    // chrome popping back up → re-show the sound hint (while muted)
    if (document.body.classList.contains('ui-quiet')) showSoundHint();
    document.body.classList.remove('ui-quiet');
    clearTimeout(quietTimer);
    quietTimer = setTimeout(() => {
      // never hide chrome while a sheet/drawer is open
      if (!document.body.classList.contains('modal-open')) {
        document.body.classList.add('ui-quiet');
      } else {
        wakeUi();
      }
    }, 2500);
  }
  function wireQuietMode() {
    ['touchstart', 'mousedown', 'keydown'].forEach(ev =>
      window.addEventListener(ev, wakeUi, { passive: true }));
    window.addEventListener('scroll', wakeUi, { passive: true });

    /* DESKTOP: hovering the nav zone brings the hidden chrome back.
       The faded elements are pointer-events:none while quiet, so we
       can't listen on them — instead we watch the cursor and wake
       whenever it's over the strip where the header + nav bubbles
       live (their layout box is still there at opacity 0). Only on
       real mouse devices; touch screens keep tap-to-wake. */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let hoverIdle = true;                       // micro-throttle
      window.addEventListener('mousemove', e => {
        if (!hoverIdle) return;
        hoverIdle = false;
        requestAnimationFrame(() => { hoverIdle = true; });
        const bub = ($subBubbles?.offsetParent ? $subBubbles : $bubbles)
          ?.getBoundingClientRect();
        const limit = bub && bub.bottom > 0 ? bub.bottom + 16 : 180;
        if (e.clientY <= limit) wakeUi();
      }, { passive: true });
    }

    wakeUi();
  }

  /* VIEWPORT (final architecture, 2026-09-09): the DOCUMENT is the
     scroller — html has scroll-snap, slides are 100dvh, body is in
     normal flow. This matches how ordinary websites behave, which is
     the durable fix for the iOS-26/Chrome-iOS "page renders under
     the browser bars after an external-app handoff" bug: a normal
     scrolling document lets the browser settle its own bars, exactly
     like every site that does NOT show the glitch. No pinned body,
     no viewport measuring, no clamps.*/
  function wireViewportHeight() {
    // keep the active slide snapped when the viewport height changes
    // (bar collapse, rotation)
    let t = null;
    const realign = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (document.body.classList.contains('modal-open')) return;
        if (!state.activeId) return;
        const active = $feed.querySelector(
          `.slide[data-id="${CSS.escape(state.activeId)}"]`);
        active?.scrollIntoView({ block: 'start' });
      }, 150);
    };
    window.addEventListener('resize', realign);
    window.addEventListener('orientationchange', realign);

    /* AUTO-DRAIN for the Chrome-iOS handoff misplacement: scrolling
       the document is what heals it (user-verified) — so scroll it
       ourselves. v8 failed because down+up in the same task nets to
       zero and the native scroll layer never moves. v9: really move
       to 2px, hold one painted frame, then return — so the browser's
       scroll machinery genuinely engages. Also fires on the fan's
       first touch (gesture context = Chrome's strongest re-layout
       trigger). Stops for good once the fan has scrolled the feed. */
    let fanScrolled = false;
    window.addEventListener('scroll', () => {
      if (window.scrollY > 8) fanScrolled = true;
    }, { passive: true });
    const drain = () => {
      if (fanScrolled) return;
      window.scrollTo(0, 2);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!fanScrolled && window.scrollY <= 8) window.scrollTo(0, 0);
      }));
    };
    drain();
    window.addEventListener('load', drain);
    [150, 400, 800, 1500, 2500, 4000].forEach(ms => setTimeout(drain, ms));
    const touchDrain = () => {
      window.removeEventListener('touchend', touchDrain);
      drain();
    };
    window.addEventListener('touchend', touchDrain, { passive: true });

    /* PRE-SWIPE BOOT — touch devices. The top chrome (logo + nav
       bubbles + sound/cart) stays hidden until the fan's first
       swipe; a translucent SWIPE TO SCROLL hint floats over the
       first video instead. Why: Chrome-iOS can misplace its native
       web view after an external-app link handoff and only a real
       user gesture re-lays it out (every programmatic trick was
       tested on device and failed). With no chrome pinned to the
       top edge, the misplacement has nothing visible to cut off —
       and the first swipe both heals the layout and reveals the
       nav. Doubles as onboarding for every mobile fan. */
    if (navigator.maxTouchPoints > 0 || 'ontouchstart' in window) {
      document.body.classList.add('pre-swipe');
      const hint = document.createElement('div');
      hint.id = 'swipeHint';
      hint.innerHTML =
        '<div class="hint__name">Obed Padilla</div>' +
        '<div class="hint__chev">&#9650;</div>' +
        '<div class="hint__text">Swipe Up</div>';
      document.body.appendChild(hint);
      const reveal = () => {
        window.removeEventListener('scroll', onFirstScroll);
        document.body.classList.remove('pre-swipe');
        hint.classList.add('gone');
        setTimeout(() => hint.remove(), 600);
      };
      const onFirstScroll = () => {
        if (window.scrollY > 8) reveal();
      };
      window.addEventListener('scroll', onFirstScroll, { passive: true });
      /* the auto-drain above may nudge scrollY briefly — that's why
         the threshold sits above the 2px drain hop. Also reveal on
         any real swipe gesture even if it lands back on slide one. */
      let touchY = null;
      window.addEventListener('touchstart',
        e => { touchY = e.touches[0].clientY; }, { passive: true });
      window.addEventListener('touchmove', e => {
        if (touchY !== null &&
            Math.abs(e.touches[0].clientY - touchY) > 24) reveal();
      }, { passive: true });
    }

  }

  /* ---- public ---- */
  function init() {
    $feed = document.getElementById('feed');
    wireViewportHeight();
    $bubbles = document.getElementById('bubbles');
    $subBubbles = document.getElementById('subBubbles');
    $soundToggle = document.getElementById('soundToggle');
    $soundToggle.addEventListener('click', toggleSound);
    wireSoundAttention();
    renderBubbles();
    renderSubBubbles();
    renderFeed();
    wireKeyboard();
    wireQuietMode();
    applyRoute();   // honor ?tab= / ?v= deep links (after pre-swipe wiring)
  }

  /** interactions.js calls this to refresh a rail count in place */
  function updateRailCount(itemId, action, text) {
    const el = $feed.querySelector(
      `.slide[data-id="${itemId}"] [data-action="${action}"] .rail__count`);
    if (el) el.textContent = text;
  }

  /** Full re-render (used after the Laylo unlock so the Backstage
      tab and bubble locks update immediately). */
  function refresh() { renderBubbles(); renderSubBubbles(); renderFeed(); }
  /** Chrome-only re-render (bubble lock badges) without touching
      the feed's scroll position. */
  function refreshChrome() { renderBubbles(); }

  return { init, updateRailCount, setFilter, refresh, refreshChrome };
})();



